// Per-thesis barometer data for the ThesisDetail chart.
// Each thesis gets its own cohorts (perspectives), global events, and insight text.
// t values: 0 = MSC Conference · 1 = Townhall Paris · 2 = Townhall Oxford (current)

const C = {
  green:  '#7ECBA5',
  blue:   '#86CBF1',
  amber:  '#D4A244',
  salmon: '#E88B7A',
};

export const TOPIC_LABELS = {
  transatlantic: { en: 'Transatlantic Relations', de: 'Transatlantische Beziehungen', fr: 'Relations transatlantiques' },
  eu_defence:    { en: 'European Defence',         de: 'Europäische Verteidigung',     fr: 'Défense européenne'         },
  ai:            { en: 'Artificial Intelligence',  de: 'Künstliche Intelligenz',        fr: 'Intelligence artificielle'  },
  ukraine:       { en: 'Ukraine',                  de: 'Ukraine',                       fr: 'Ukraine'                    },
};

// cohort shape:
//   id, color
//   label:      { en, de, fr }   - full label shown on tablet + popup
//   labelShort: { en, de, fr }   - short label for mobile chart overlay
//   blurb:      { en }           - description shown in "Who's behind" popup
//   source:     { en }           - verification note shown in popup
//   series:     [{ t, v }]       - time-series agreement data (t=0 MSC, t=1 Paris, t=2 Oxford)

// insight shape: array of two lines, each line is an array of strings and
//   { id: cohortId, text: string } objects (rendered colored in the chart).

export const THESIS_BARO_DATA = {

  // ── t2: The US remains Europe's most important security partner ───────────
  t2: {
    // Earlier top-timeline stations (only reachable once you pan into 2025).
    stations2025: [
      { id: 'msc25', dateISO: '2025-02-14', flagship: true, label: 'MSC', sub: 'Feb 2025' },
    ],
    cohorts: [
      {
        id: 'experts', color: C.green,
        label:      { en: 'Experts',       de: 'Experten',              fr: 'Experts'        },
        labelShort: { en: 'Experts',       de: 'Experten',              fr: 'Experts'        },
        blurb:  { en: 'Security-policy researchers and practitioners invited by the MSC.' },
        source: { en: 'Invited & verified by the MSC' },
        preV: 76, postV: 59,
        series: [
          { t: 0.00, v: 73 }, { t: 0.21, v: 73 }, { t: 0.35, v: 71 },
          { t: 0.60, v: 70 }, { t: 0.80, v: 69 }, { t: 0.90, v: 61 },
          { t: 1.00, v: 61 }, { t: 1.50, v: 60 }, { t: 2.00, v: 60 },
        ],
        series2025: [
          { date: '2025-01-15', v: 84 }, { date: '2025-02-25', v: 83 },
          { date: '2025-03-10', v: 79 }, { date: '2025-06-18', v: 78 },
          { date: '2025-07-02', v: 80 }, { date: '2025-11-18', v: 79 },
          { date: '2025-12-02', v: 76 }, { date: '2025-12-28', v: 76 },
        ],
      },
      {
        id: 'participants', color: C.blue,
        label:      { en: 'In the Room',   de: 'Im Saal',               fr: 'Dans la salle'  },
        labelShort: { en: 'In the Room',   de: 'Im Saal',               fr: 'Dans la salle'  },
        blurb:  { en: 'Accredited delegates attending the MSC and European Defence Townhalls.' },
        source: { en: 'Verified on site by the MSC' },
        preV: 72, postV: 49,
        series: [
          { t: 0.00, v: 68 }, { t: 0.21, v: 68 }, { t: 0.35, v: 65 },
          { t: 0.60, v: 64 }, { t: 0.80, v: 63 }, { t: 0.90, v: 51 },
          { t: 1.00, v: 51 }, { t: 1.50, v: 51 }, { t: 2.00, v: 51 },
        ],
        series2025: [
          { date: '2025-01-15', v: 82 }, { date: '2025-02-25', v: 81 },
          { date: '2025-03-10', v: 76 }, { date: '2025-06-18', v: 75 },
          { date: '2025-07-02', v: 77 }, { date: '2025-11-18', v: 76 },
          { date: '2025-12-02', v: 72 }, { date: '2025-12-28', v: 72 },
        ],
      },
      {
        id: 'future_leaders', color: C.amber,
        label:      { en: 'Students',  de: 'Studierende',  fr: 'Étudiants'   },
        labelShort: { en: 'Students',  de: 'Studierende',            fr: 'Étudiants'     },
        blurb:  { en: 'Students from universities across Europe.' },
        source: { en: 'Selected & verified by the MSC' },
        preV: 70, postV: 39,
        series: [
          { t: 0.00, v: 66 }, { t: 0.21, v: 65 }, { t: 0.35, v: 53 },
          { t: 0.60, v: 49 }, { t: 0.80, v: 46 }, { t: 0.90, v: 43 },
          { t: 1.00, v: 42 }, { t: 1.50, v: 41 }, { t: 2.00, v: 41 },
        ],
        series2025: [
          { date: '2025-01-15', v: 80 }, { date: '2025-02-25', v: 79 },
          { date: '2025-03-10', v: 71 }, { date: '2025-06-18', v: 70 },
          { date: '2025-07-02', v: 71 }, { date: '2025-11-18', v: 71 },
          { date: '2025-12-02', v: 70 }, { date: '2025-12-28', v: 70 },
        ],
      },
      {
        id: 'public', color: C.salmon,
        label:      { en: 'Broad Public',  de: 'Breite Öffentlichkeit', fr: 'Grand public'   },
        labelShort: { en: 'Broad Public',  de: 'Breite Öff.',           fr: 'Grand pub.'     },
        blurb:  { en: 'Members of the public following the conference, of all ages.' },
        source: { en: 'Phone-verified representative sample' },
        preV: 43, postV: 18,
        series: [
          { t: 0.00, v: 40 }, { t: 0.21, v: 39 }, { t: 0.35, v: 30 },
          { t: 0.60, v: 27 }, { t: 0.80, v: 25 }, { t: 0.90, v: 22 },
          { t: 1.00, v: 21 }, { t: 1.50, v: 20 }, { t: 2.00, v: 20 },
        ],
        series2025: [
          { date: '2025-01-15', v: 58 }, { date: '2025-02-25', v: 56 },
          { date: '2025-03-10', v: 47 }, { date: '2025-06-18', v: 46 },
          { date: '2025-07-02', v: 47 }, { date: '2025-11-18', v: 46 },
          { date: '2025-12-02', v: 43 }, { date: '2025-12-28', v: 43 },
        ],
      },
    ],
    globalEvents: [
      {
        id: 'e25a', dateISO: '2025-02-28', tier: 2,
        label: 'Trump-Zelensky clash', date: 'Feb 28, 2025',
        note: "In a televised Oval Office meeting, Trump and Vance berated President Zelensky - 'you don't have the cards... make a deal or we're out' - before cutting the visit short. Broadcast worldwide, it was the moment many Europeans concluded Washington could no longer be relied on.",
      },
      {
        id: 'e25b', dateISO: '2025-06-24', tier: 1,
        label: 'NATO Hague Summit', date: 'Jun 24, 2025',
        note: "At The Hague, every NATO ally but Spain pledged to lift defence spending toward 5% of GDP by 2035 and reaffirmed the 'ironclad' Article 5 commitment. The functioning summit steadied nerves - a brief reminder that the alliance, and the US role in it, still held.",
      },
      {
        id: 'e25c', dateISO: '2025-11-21', tier: 2,
        label: 'US-Russia peace plan', date: 'Nov 21, 2025',
        note: "A 28-point US plan for Ukraine - drafted by Trump's envoys and widely seen as favourable to Moscow - leaked, with Europe pressed to accept terms it had no hand in shaping. The sense that Washington would bargain with Russia over Europe's head reopened the slide in trust.",
      },
      {
        id: 'g2', t: -0.15, tier: 1,
        label: 'US National Defense Strategy', date: 'Jan 23, 2026',
        note: 'The Pentagon\'s new National Defense Strategy explicitly ranked China and homeland defence above Europe. The document reframed Washington\'s role from alliance leader to "enabler" - and declared that Europe must take primary responsibility for conventional deterrence against Russia.',
      },
      {
        id: 'g4', t: 0.26, tier: 2,
        label: 'US strikes Iran', date: 'Mar 12, 2026',
        note: 'Joint US-Israeli strikes killed Supreme Leader Khamenei and triggered a regional conflict across the Middle East. With two carrier strike groups deployed to the region, US military attention and diplomatic bandwidth shifted decisively away from Europe and Ukraine - leaving European capitals to navigate the security crisis largely on their own.',
      },
      {
        id: 'g3', t: 0.8, tier: 2,
        label: 'US troop withdrawal', date: 'May 19, 2026',
        note: 'Trump announces withdrawal of 5.000 US forces from Germany and cancelled the planned deployment of Tomahawk and hypersonic missiles to Europe - the first such stationing since the INF treaty in 1987. The move widened Europe\'s deterrence gap against Russia and caught NATO allies off guard.',
      },
    ],
    insight: [
      ["Today's widest split: ", { id: 'experts', text: 'Experts' }, " and ", { id: 'public', text: 'Broad Public' }, ", 40 points apart."],
      ["The steepest fall is ", { id: 'future_leaders', text: 'Students' }, " (−25 pts), with ", { id: 'public', text: 'Broad Public' }, " close behind (−20)."],
    ],
  },

  // ── t1: Europe must guarantee its security independently of the US ────────
  // Key split: Eastern vs Western European delegates - proximity to Russia matters.
  t1: {
    cohorts: [
      {
        id: 'eastern_eu', color: C.green,
        label:      { en: 'Eastern Europe',  de: 'Osteuropa',    fr: "Europe de l'Est"    },
        labelShort: { en: 'Eastern EU',      de: 'Osteuropa',    fr: 'Europe Est'         },
        blurb:  { en: 'Conference delegates from Poland, the Baltic states, Finland and Romania - states bordering Russia or historically under Soviet influence.' },
        source: { en: 'Verified on site by the MSC' },
        preV: 81, postV: 93,
        series: [
          { t: 0.00, v: 85 }, { t: 1.00, v: 87 }, { t: 1.13, v: 87 },
          { t: 1.20, v: 92 }, { t: 1.50, v: 92 }, { t: 2.00, v: 91 },
        ],
      },
      {
        id: 'western_eu', color: C.blue,
        label:      { en: 'Western Europe',  de: 'Westeuropa',   fr: "Europe de l'Ouest"  },
        labelShort: { en: 'Western EU',      de: 'Westeuropa',   fr: 'Europe Ouest'       },
        blurb:  { en: 'Conference delegates from Germany, France, Italy, Spain and the Benelux states.' },
        source: { en: 'Verified on site by the MSC' },
        preV: 54, postV: 79,
        series: [
          { t: 0.00, v: 58 }, { t: 1.00, v: 62 }, { t: 1.13, v: 62 },
          { t: 1.20, v: 74 }, { t: 1.50, v: 75 }, { t: 2.00, v: 76 },
        ],
      },
      {
        id: 'young_public', color: C.amber,
        label:      { en: 'Students',    de: 'Studierende',  fr: 'Étudiants' },
        labelShort: { en: 'Students',    de: 'Studierende',            fr: 'Étudiants'   },
        blurb:  { en: 'Students from universities across Europe.' },
        source: { en: 'Selected & verified by the MSC' },
        preV: 63, postV: 85,
        series: [
          { t: 0.00, v: 68 }, { t: 1.00, v: 72 }, { t: 1.13, v: 72 },
          { t: 1.20, v: 82 }, { t: 1.50, v: 82 }, { t: 2.00, v: 82 },
        ],
      },
      {
        id: 'broad_public', color: C.salmon,
        label:      { en: 'Broad Public',    de: 'Breite Öffentlichkeit', fr: 'Grand public' },
        labelShort: { en: 'Broad Public',    de: 'Breite Öff.',           fr: 'Grand pub.'   },
        blurb:  { en: 'Members of the public following the conference, of all ages.' },
        source: { en: 'Phone-verified representative sample' },
        preV: 41, postV: 66,
        series: [
          { t: 0.00, v: 45 }, { t: 1.00, v: 50 }, { t: 1.13, v: 50 },
          { t: 1.20, v: 62 }, { t: 1.50, v: 63 }, { t: 2.00, v: 63 },
        ],
      },
    ],
    globalEvents: [
      {
        id: 'g1', t: 1.13, tier: 1, demo: true,
        label: 'US Troop withdrawal', date: 'Feb 2025',
        note: 'The US announces a phased 30 % reduction of troops in Europe. For many, this makes strategic autonomy a necessity rather than an aspiration.',
      },
      // {
      //   id: 'g2', t: 1.32, tier: 1,
      //   label: 'EU Defence Budget +2.5 %', date: 'Apr 2025',
      //   note: 'The European Council agrees binding defence spending of 2.5 % of GDP - a concrete first institutional step toward strategic independence.',
      // },
    ],
    insight: [
      [{ id: 'eastern_eu', text: 'Eastern Europe' }, " jumped to 91%; US withdrawal news also converted many ", { id: 'western_eu', text: 'Western Europeans' }, " (+18 pts)."],
      ["Today's 28-point gap between Eastern delegates and the ", { id: 'broad_public', text: 'Broad Public' }, " (63%) reflects a persistent insider–outsider divide."],
    ],
  },

  // ── t3: AI must be more strictly regulated internationally ────────────────
  // Key split: industry delegates resist regulation; civil society and the public demand it.
  t3: {
    cohorts: [
      {
        id: 'ai_experts', color: C.green,
        label:      { en: 'AI Experts',       de: 'KI-Experten',         fr: 'Experts en IA'   },
        labelShort: { en: 'AI Experts',       de: 'KI-Experten',         fr: 'Experts IA'      },
        blurb:  { en: 'AI researchers, ethicists and technical safety specialists invited by the MSC.' },
        source: { en: 'Invited & verified by the MSC' },
        preV: 74, postV: 89,
        series: [
          { t: 0.00, v: 78 }, { t: 0.20, v: 80 }, { t: 0.70, v: 82 },
          { t: 0.90, v: 86 }, { t: 1.00, v: 86 }, { t: 2.00, v: 88 },
        ],
      },
      {
        id: 'tech_industry', color: C.blue,
        label:      { en: 'Tech Industry',     de: 'Technologiebranche',  fr: 'Industrie tech'  },
        labelShort: { en: 'Tech Industry',     de: 'Tech-Branche',        fr: 'Tech'            },
        blurb:  { en: 'Delegates from technology companies and digital industry associations attending the conference.' },
        source: { en: 'Invited & verified by the MSC' },
        preV: 56, postV: 39,
        series: [
          { t: 0.00, v: 52 }, { t: 0.20, v: 48 }, { t: 0.70, v: 47 },
          { t: 0.90, v: 43 }, { t: 1.00, v: 43 }, { t: 2.00, v: 40 },
        ],
      },
      {
        id: 'civil_society', color: C.amber,
        label:      { en: 'Civil Society',     de: 'Zivilgesellschaft',   fr: 'Société civile'  },
        labelShort: { en: 'Civil Society',     de: 'Zivilges.',           fr: 'Société civ.'    },
        blurb:  { en: 'NGO representatives, digital rights advocates and ethicists attending the conference.' },
        source: { en: 'Invited & verified by the MSC' },
        preV: 79, postV: 91,
        series: [
          { t: 0.00, v: 82 }, { t: 0.20, v: 85 }, { t: 0.70, v: 86 },
          { t: 0.90, v: 89 }, { t: 1.00, v: 89 }, { t: 2.00, v: 90 },
        ],
      },
      {
        id: 'broad_public', color: C.salmon,
        label:      { en: 'Broad Public',      de: 'Breite Öffentlichkeit', fr: 'Grand public'  },
        labelShort: { en: 'Broad Public',      de: 'Breite Öff.',           fr: 'Grand pub.'    },
        blurb:  { en: 'Members of the public following the conference, of all ages.' },
        source: { en: 'Phone-verified representative sample' },
        preV: 71, postV: 85,
        series: [
          { t: 0.00, v: 76 }, { t: 0.20, v: 79 }, { t: 0.70, v: 80 },
          { t: 0.90, v: 83 }, { t: 1.00, v: 83 }, { t: 2.00, v: 84 },
        ],
      },
    ],
    globalEvents: [
      {
        id: 'g1', t: -0.22, tier: 1,
        label: 'Grok deepfake scandal', date: 'Jan 2026',
        note: "xAI's Grok image tool floods X with non-consensual sexual deepfakes - including images appearing to depict minors - prompting French prosecutors to raid X's Paris offices and an Irish Data Protection Commission inquiry. The scandal makes AI's real-world harms visceral and drives global calls for tougher rules.",
      },
      {
        id: 'g2', t: 0.18, tier: 2,
        label: 'EU AI-content labelling code', date: 'Mar 3, 2026',
        note: "The EU publishes its Code of Practice for marking and labelling AI-generated content under the AI Act - but only as a voluntary transparency tool with no binding force. After the Grok deepfake wave, regulation advocates read the soft response as proof that codes of practice won't be enough.",
      },
      {
        id: 'g3', t: 0.78, tier: 1,
        label: 'EU delays AI Act', date: 'May 6, 2026',
        note: "Under industry pressure, the EU's 'Digital Omnibus' deal postpones the AI Act's high-risk obligations - pushing stand-alone Annex III rules back to December 2027, just months before they were due to apply. The retreat of the world's benchmark AI regulator galvanises calls for binding international standards.",
      },
    ],
    insight: [
      [{ id: 'civil_society', text: 'Civil Society' }, " (90%) now sits 50 points above ", { id: 'tech_industry', text: 'Tech Industry' }, " (40%) - the widest split of the survey."],
      ["Each half-measure - a voluntary EU labelling code, then its delayed AI Act - hardened the ", { id: 'broad_public', text: 'Broad Public' }, " while ", { id: 'tech_industry', text: 'Tech Industry' }, " drifted the other way."],
    ],
  },

  // ── t4: Over-regulating AI risks losing the technological race ────────────
  // Key split: tech industry vs civil society/ethicists - economic stake drives the divide.
  t4: {
    cohorts: [
      {
        id: 'tech_industry', color: C.green,
        label:      { en: 'Tech Industry',         de: 'Technologiebranche',  fr: 'Industrie tech'     },
        labelShort: { en: 'Tech Industry',         de: 'Tech-Branche',        fr: 'Tech'               },
        blurb:  { en: 'Delegates from technology companies and digital industry associations.' },
        source: { en: 'Invited & verified by the MSC' },
        preV: 82, postV: 91,
        series: [
          { t: 0.00, v: 85 }, { t: 0.30, v: 85 }, { t: 0.60, v: 86 },
          { t: 0.72, v: 89 }, { t: 0.85, v: 90 }, { t: 1.00, v: 90 }, { t: 2.00, v: 90 },
        ],
      },
      {
        id: 'geo_experts', color: C.blue,
        label:      { en: 'Geopolitical Experts',   de: 'Geopolitikexperten',  fr: 'Experts géopol.'    },
        labelShort: { en: 'Geo. Experts',           de: 'Geopolitikexp.',      fr: 'Géopol.'            },
        blurb:  { en: 'Security-policy researchers and strategists focused on the geopolitics of emerging technologies.' },
        source: { en: 'Invited & verified by the MSC' },
        preV: 64, postV: 81,
        series: [
          { t: 0.00, v: 68 }, { t: 0.30, v: 69 }, { t: 0.60, v: 70 },
          { t: 0.72, v: 77 }, { t: 0.85, v: 79 }, { t: 1.00, v: 80 }, { t: 2.00, v: 80 },
        ],
      },
      {
        id: 'civil_society', color: C.amber,
        label:      { en: 'Civil Society',           de: 'Zivilgesellschaft',   fr: 'Société civile'     },
        labelShort: { en: 'Civil Society',           de: 'Zivilges.',           fr: 'Société civ.'       },
        blurb:  { en: 'NGO representatives, digital rights advocates and ethicists at the conference.' },
        source: { en: 'Invited & verified by the MSC' },
        preV: 34, postV: 25,
        series: [
          { t: 0.00, v: 32 }, { t: 0.30, v: 31 }, { t: 0.60, v: 31 },
          { t: 0.72, v: 29 }, { t: 0.85, v: 27 }, { t: 1.00, v: 27 }, { t: 2.00, v: 26 },
        ],
      },
      {
        id: 'young_public', color: C.salmon,
        label:      { en: 'Students',            de: 'Studierende', fr: 'Étudiants'      },
        labelShort: { en: 'Students',            de: 'Studierende',           fr: 'Étudiants'        },
        blurb:  { en: 'Students from universities across Europe.' },
        source: { en: 'Selected & verified by the MSC' },
        preV: 53, postV: 64,
        series: [
          { t: 0.00, v: 55 }, { t: 0.30, v: 56 }, { t: 0.60, v: 57 },
          { t: 0.72, v: 61 }, { t: 0.85, v: 62 }, { t: 1.00, v: 62 }, { t: 2.00, v: 63 },
        ],
      },
    ],
    globalEvents: [
      {
        id: 'g1', t: -0.15, tier: 1,
        label: "Europe's AI gap", date: 'Jan 27, 2026',
        note: "Analyses ahead of the MSC lay bare how far Europe trails in the AI race: the US has produced roughly 40 foundation models and China 15, while all of Europe combined has just three. Fragmented across 27 markets, the continent is judged to be leading on rules but losing on capability.",
      },
      {
        id: 'g2', t: 0.67, tier: 1,
        label: 'DeepSeek V4', date: 'Apr 24, 2026',
        note: "China's DeepSeek previews V4 - a 1.6-trillion-parameter open-source model priced around four times cheaper than US rivals and trailing the frontier by only 3-6 months. A year after R1 upended the industry, it shows China closing the gap fast and sharpens fears that Europe cannot afford to over-regulate.",
      },
      {
        id: 'g3', t: 0.78, tier: 2,
        label: 'EU pauses the AI Act', date: 'May 6, 2026',
        note: "The EU's 'Digital Omnibus' deal postpones the AI Act's high-risk obligations - explicitly to ease the burden on industry and protect competitiveness. For race-watchers, Brussels concedes their point; for civil society, it is deregulation by stealth dressed up as strategy.",
      },
    ],
    insight: [
      [{ id: 'tech_industry', text: 'Tech Industry' }, " (90%) and ", { id: 'civil_society', text: 'Civil Society' }, " (26%) now sit 64 points apart - the sharpest divide of the survey."],
      ["DeepSeek's V4 made ", { id: 'geo_experts', text: 'Geopolitical Experts' }, " the biggest movers (+12); ", { id: 'civil_society', text: 'Civil Society' }, " dug in, reading every 'race' argument as a licence to deregulate."],
    ],
  },

  // ── t5: Europe must double its defence spending by 2035 ──────────────────
  // Key split: conference insiders (security-focused) vs general public (cost concerns).
  t5: {
    cohorts: [
      {
        id: 'participants', color: C.green,
        label:      { en: 'In the Room',       de: 'Im Saal',               fr: 'Dans la salle'  },
        labelShort: { en: 'In the Room',       de: 'Im Saal',               fr: 'Dans la salle'  },
        blurb:  { en: 'Accredited delegates attending the MSC and the European Defence Townhall series.' },
        source: { en: 'Verified on site by the MSC' },
        preV: 78, postV: 95,
        series: [
          { t: 0.00, v: 82 }, { t: 1.00, v: 86 }, { t: 1.13, v: 86 },
          { t: 1.20, v: 92 }, { t: 1.32, v: 93 }, { t: 2.00, v: 93 },
        ],
      },
      {
        id: 'experts', color: C.blue,
        label:      { en: 'Defence Experts',   de: 'Verteidigungsexperten', fr: 'Experts défense' },
        labelShort: { en: 'Def. Experts',      de: 'Verteidigg.-Exp.',      fr: 'Experts déf.'   },
        blurb:  { en: 'Security-policy and defence researchers, strategists and former military officials invited by the MSC.' },
        source: { en: 'Invited & verified by the MSC' },
        preV: 67, postV: 89,
        series: [
          { t: 0.00, v: 72 }, { t: 1.00, v: 78 }, { t: 1.13, v: 78 },
          { t: 1.20, v: 86 }, { t: 1.32, v: 87 }, { t: 2.00, v: 87 },
        ],
      },
      {
        id: 'young_public', color: C.amber,
        label:      { en: 'Students',      de: 'Studierende',  fr: 'Étudiants'   },
        labelShort: { en: 'Students',      de: 'Studierende',            fr: 'Étudiants'     },
        blurb:  { en: 'Students from universities across Europe.' },
        source: { en: 'Selected & verified by the MSC' },
        preV: 43, postV: 71,
        series: [
          { t: 0.00, v: 48 }, { t: 1.00, v: 54 }, { t: 1.13, v: 54 },
          { t: 1.20, v: 66 }, { t: 1.32, v: 68 }, { t: 2.00, v: 68 },
        ],
      },
      {
        id: 'broad_public', color: C.salmon,
        label:      { en: 'Broad Public',      de: 'Breite Öffentlichkeit', fr: 'Grand public'   },
        labelShort: { en: 'Broad Public',      de: 'Breite Öff.',           fr: 'Grand pub.'     },
        blurb:  { en: 'Members of the public of all ages following the conference.' },
        source: { en: 'Phone-verified representative sample' },
        preV: 30, postV: 45,
        series: [
          { t: 0.00, v: 34 }, { t: 1.00, v: 36 }, { t: 1.13, v: 36 },
          { t: 1.20, v: 42 }, { t: 1.32, v: 43 }, { t: 2.00, v: 43 },
        ],
      },
    ],
    globalEvents: [
      {
        id: 'g1', t: 1.13, tier: 1, demo: true,
        label: 'US Troops −30 %', date: 'Feb 2025',
        note: 'The US announces a 30 % troop reduction in Europe. For many conference delegates, this converts abstract support for doubling defence budgets into urgent political necessity.',
      },
      {
        id: 'g2', t: 1.32, tier: 1,
        label: 'EU Defence Budget +2.5 %', date: 'Apr 2025',
        note: 'The European Council agrees binding defence spending of 2.5 % of GDP for all member states by 2028 - a concrete first step toward the doubling target.',
      },
    ],
    insight: [
      [{ id: 'participants', text: 'In the Room' }, " reached 93% - driven to near-consensus by the US withdrawal announcement."],
      ["A 50-point gap separates the conference room from the ", { id: 'broad_public', text: 'Broad Public' }, "; even ", { id: 'young_public', text: 'Students' }, " gained 20 pts from the shock."],
    ],
  },

  // ── t6: Higher defence spending worth cutting other public spending ────────
  // Key split: conference audience ready for trade-offs vs public defending welfare spending.
  t6: {
    cohorts: [
      {
        id: 'participants', color: C.green,
        label:      { en: 'In the Room',        de: 'Im Saal',               fr: 'Dans la salle'  },
        labelShort: { en: 'In the Room',        de: 'Im Saal',               fr: 'Dans la salle'  },
        blurb:  { en: 'Accredited delegates at the MSC and European Defence Townhalls - a security-focused audience primed to accept difficult trade-offs.' },
        source: { en: 'Verified on site by the MSC' },
        preV: 75, postV: 87,
        series: [
          { t: 0.00, v: 77 }, { t: 0.15, v: 77 }, { t: 0.25, v: 80 },
          { t: 0.68, v: 80 }, { t: 0.80, v: 85 }, { t: 1.00, v: 85 },
          { t: 2.00, v: 86 },
        ],
      },
      {
        id: 'experts', color: C.blue,
        label:      { en: 'Defence Experts',    de: 'Verteidigungsexperten', fr: 'Experts défense' },
        labelShort: { en: 'Def. Experts',       de: 'Verteidigg.-Exp.',      fr: 'Experts déf.'   },
        blurb:  { en: 'Security-policy researchers and former military officials invited by the MSC.' },
        source: { en: 'Invited & verified by the MSC' },
        preV: 54, postV: 59,
        series: [
          { t: 0.00, v: 55 }, { t: 0.15, v: 55 }, { t: 0.25, v: 57 },
          { t: 0.68, v: 57 }, { t: 0.80, v: 58 }, { t: 1.00, v: 58 },
          { t: 2.00, v: 58 },
        ],
      },
      {
        id: 'young_public', color: C.amber,
        label:      { en: 'Students',       de: 'Studierende',  fr: 'Étudiants'   },
        labelShort: { en: 'Students',       de: 'Studierende',            fr: 'Étudiants'     },
        blurb:  { en: 'Students from universities across Europe.' },
        source: { en: 'Selected & verified by the MSC' },
        preV: 27, postV: 16,
        series: [
          { t: 0.00, v: 26 }, { t: 0.15, v: 26 }, { t: 0.25, v: 23 },
          { t: 0.68, v: 23 }, { t: 0.80, v: 18 }, { t: 1.00, v: 18 },
          { t: 2.00, v: 17 },
        ],
      },
      {
        id: 'broad_public', color: C.salmon,
        label:      { en: 'Broad Public',       de: 'Breite Öffentlichkeit', fr: 'Grand public'   },
        labelShort: { en: 'Broad Public',       de: 'Breite Öff.',           fr: 'Grand pub.'     },
        blurb:  { en: 'Members of the public of all ages following the conference.' },
        source: { en: 'Phone-verified representative sample' },
        preV: 19, postV: 9,
        series: [
          { t: 0.00, v: 18 }, { t: 0.15, v: 18 }, { t: 0.25, v: 15 },
          { t: 0.68, v: 15 }, { t: 0.80, v: 11 }, { t: 1.00, v: 11 },
          { t: 2.00, v: 10 },
        ],
      },
    ],
    globalEvents: [
      {
        id: 'g1', t: 0.18, tier: 1,
        label: 'UK Spring Statement', date: 'Mar 3, 2026',
        note: "Chancellor Rachel Reeves steered UK defence toward 3% of GDP while announcing deeper welfare cuts - freezing the health element of Universal Credit and tightening PIP disability eligibility. Around 3.2 million families are projected to lose an average of £1,720 a year, making the guns-versus-welfare trade-off explicit.",
      },
      {
        id: 'g2', t: 0.71, tier: 2,
        label: 'Germany slashes welfare', date: 'Apr 28, 2026',
        note: "Merz's coalition approved €38.3 billion in welfare cuts by 2030 - scaling the state pension back to a basic 'old-age cover', raising health co-payments and trimming long-term unemployment benefits - explicitly to help fund rearmament. With 69% of Germans fearing old-age poverty, the cuts triggered a public backlash and a surge for the AfD.",
      },
    ],
    insight: [
      ["As the trade-off turned real, ", { id: 'participants', text: 'In the Room' }, " hardened to 86% while the ", { id: 'broad_public', text: 'Broad Public' }, " fell to 10% - a 76-point gulf."],
      [{ id: 'young_public', text: 'Students' }, " moved opposite the room: as disability and pension cuts began to bite, their support dropped nine points."],
    ],
  },

  // ── t9: If too few volunteer, military service should become compulsory ───
  // Key split: generational - older generations support it, the young who would serve oppose it.
  t9: {
    cohorts: [
      {
        id: 'age_61plus', color: C.green,
        label:      { en: '61 and older',  de: '61 und älter',  fr: '61 ans et plus' },
        labelShort: { en: '61 and older',  de: '61 und älter',  fr: '61 et plus'     },
        blurb:  { en: 'Survey respondents aged 61 and above, weighted to match national demographics.' },
        source: { en: 'Phone-verified, nationally representative' },
        preV: 71, postV: 83,
        series: [
          { t: 0.00, v: 73 }, { t: 0.30, v: 73 }, { t: 0.45, v: 76 },
          { t: 1.00, v: 77 }, { t: 1.25, v: 77 }, { t: 1.45, v: 81 },
          { t: 2.00, v: 82 },
        ],
      },
      {
        id: 'age_41_60', color: C.blue,
        label:      { en: '41 to 60',      de: '41 bis 60',     fr: '41 à 60 ans'    },
        labelShort: { en: '41–60',         de: '41–60',         fr: '41–60'          },
        blurb:  { en: 'Survey respondents aged 41–60, weighted to match national demographics.' },
        source: { en: 'Phone-verified, nationally representative' },
        preV: 67, postV: 81,
        series: [
          { t: 0.00, v: 69 }, { t: 0.30, v: 69 }, { t: 0.45, v: 72 },
          { t: 1.00, v: 74 }, { t: 1.25, v: 74 }, { t: 1.45, v: 79 },
          { t: 2.00, v: 80 },
        ],
      },
      {
        id: 'age_26_40', color: C.amber,
        label:      { en: '26 to 40',      de: '26 bis 40',     fr: '26 à 40 ans'    },
        labelShort: { en: '26–40',         de: '26–40',         fr: '26–40'          },
        blurb:  { en: 'Survey respondents aged 26–40, weighted to match national demographics.' },
        source: { en: 'Phone-verified, nationally representative' },
        preV: 48, postV: 62,
        series: [
          { t: 0.00, v: 50 }, { t: 0.30, v: 50 }, { t: 0.45, v: 53 },
          { t: 1.00, v: 55 }, { t: 1.25, v: 55 }, { t: 1.45, v: 60 },
          { t: 2.00, v: 61 },
        ],
      },
      {
        id: 'age_18_25', color: C.salmon,
        label:      { en: '18 to 25',      de: '18 bis 25',     fr: '18 à 25 ans'    },
        labelShort: { en: '18–25',         de: '18–25',         fr: '18–25'          },
        blurb:  { en: 'Survey respondents aged 18–25 - those who would be directly affected by mandatory service.' },
        source: { en: 'Phone-verified, nationally representative' },
        preV: 25, postV: 17,
        series: [
          { t: 0.00, v: 24 }, { t: 0.30, v: 24 }, { t: 0.45, v: 24 },
          { t: 1.00, v: 24 }, { t: 1.25, v: 24 }, { t: 1.45, v: 19 },
          { t: 2.00, v: 18 },
        ],
      },
    ],
    globalEvents: [
      {
        id: 'g1', t: -0.13, tier: 1,
        label: 'Military service returns', date: 'Jan 1, 2026',
        note: "Germany's Military Service Modernisation Act enters into force: every man turning 18 must complete a readiness questionnaire and medical, though service stays voluntary - with a dormant 'needs-based conscription' clause parliament can activate if targets are missed. Lithuania makes service compulsory for men aged 19-29 the same month and Croatia moves to reinstate the draft.",
      },
      {
        id: 'g2', t: 0.37, tier: 2,
        label: 'Baltic drone incursions', date: 'Mar 23, 2026',
        note: "Drones cross from Russia into Lithuanian, Latvian and Estonian airspace during Ukraine's long-range strike campaign - escalating over the following weeks until a NATO F-16 downs one over Estonia on 19 May. Moscow amplifies the incidents to pressure the eastern flank, sharpening the sense that Europe needs more troops fast.",
      },
      {
        id: 'g3', t: 1.35, tier: 1,
        label: '530 of 300,000 volunteer', date: 'Jun 2026',
        note: "Five months into the new system, the Defence Ministry reveals that of roughly 298,200 young people sent the questionnaire, only 530 have volunteered to serve - a stark shortfall that makes the law's needs-based conscription trigger suddenly realistic.",
      },
    ],
    insight: [
      ["As recruitment collapsed, every older cohort rallied behind compulsion - only ", { id: 'age_18_25', text: 'those aged 18-25' }, ", who would actually serve, moved the other way."],
      ["The 530-volunteer shortfall pushed ", { id: 'age_41_60', text: '41-60' }, " to 80%; ", { id: 'age_18_25', text: 'the youngest' }, " fell to 18% as the draft turned real."],
    ],
  },

  // ── t7: Ukraine aid should aim for military victory rather than negotiations
  // Key split: Eastern vs Western European delegates + security experts who prefer diplomacy.
  t7: {
    cohorts: [
      {
        id: 'eastern_eu', color: C.green,
        label:      { en: 'Eastern Europe',    de: 'Osteuropa',             fr: "Europe de l'Est"   },
        labelShort: { en: 'Eastern EU',        de: 'Osteuropa',             fr: 'Europe Est'        },
        blurb:  { en: 'Conference delegates from Poland, the Baltic states, Finland and Romania.' },
        source: { en: 'Verified on site by the MSC' },
        preV: 81, postV: 75,
        series: [
          { t: 0.00, v: 82 }, { t: 0.10, v: 83 }, { t: 0.78, v: 82 },
          { t: 0.92, v: 71 }, { t: 1.00, v: 71 }, { t: 1.45, v: 71 },
          { t: 1.60, v: 74 }, { t: 2.00, v: 74 },
        ],
      },
      {
        id: 'western_eu', color: C.blue,
        label:      { en: 'Western Europe',    de: 'Westeuropa',            fr: "Europe de l'Ouest" },
        labelShort: { en: 'Western EU',        de: 'Westeuropa',            fr: 'Europe Ouest'      },
        blurb:  { en: 'Conference delegates from Germany, France, Italy, Spain and the Benelux states.' },
        source: { en: 'Verified on site by the MSC' },
        preV: 62, postV: 44,
        series: [
          { t: 0.00, v: 60 }, { t: 0.10, v: 61 }, { t: 0.78, v: 60 },
          { t: 0.92, v: 45 }, { t: 1.00, v: 45 }, { t: 2.00, v: 45 },
        ],
      },
      {
        id: 'security_experts', color: C.amber,
        label:      { en: 'Security Experts',  de: 'Sicherheitsexperten',   fr: 'Experts sécurité'  },
        labelShort: { en: 'Sec. Experts',      de: 'Sicherheitsexp.',       fr: 'Experts séc.'      },
        blurb:  { en: 'Security-policy researchers and strategists with expertise in conflict resolution and peace negotiations.' },
        source: { en: 'Invited & verified by the MSC' },
        preV: 30, postV: 23,
        series: [
          { t: 0.00, v: 28 }, { t: 0.10, v: 28 }, { t: 0.78, v: 27 },
          { t: 0.92, v: 24 }, { t: 1.00, v: 24 }, { t: 2.00, v: 24 },
        ],
      },
      {
        id: 'broad_public', color: C.salmon,
        label:      { en: 'Broad Public',      de: 'Breite Öffentlichkeit', fr: 'Grand public'      },
        labelShort: { en: 'Broad Public',      de: 'Breite Öff.',           fr: 'Grand pub.'        },
        blurb:  { en: 'Members of the public following the conference, of all ages.' },
        source: { en: 'Phone-verified representative sample' },
        preV: 24, postV: 14,
        series: [
          { t: 0.00, v: 22 }, { t: 0.10, v: 22 }, { t: 0.78, v: 21 },
          { t: 0.92, v: 16 }, { t: 1.00, v: 16 }, { t: 2.00, v: 15 },
        ],
      },
    ],
    globalEvents: [
      {
        id: 'g1', t: 0.05, tier: 1,
        label: 'Ukraine regains ground', date: 'Feb 2026',
        note: "February 2026 becomes the first month since 2024 that Ukraine reclaims more territory than it loses, as Russia's spring offensive stalls and Ukraine's mid-range strike campaign hammers Russian logistics. For a moment, outright military victory looks achievable again.",
      },
      {
        id: 'g2', t: 0.80, tier: 2,
        label: 'Trump-brokered ceasefire', date: 'May 8, 2026',
        note: "Trump announces that Russia and Ukraine have agreed to a three-day ceasefire and an exchange of 1,000 prisoners each, calling it the 'beginning of the end' of the war. The first concrete truce in years makes a negotiated settlement feel suddenly within reach.",
      },
      {
        id: 'g3', t: 1.50, tier: 1,
        label: 'Ceasefire lapses', date: 'Jun 2026',
        note: "The three-day truce expires without a durable deal; fighting resumes as Ukrainian drones strike Russian supply ships and Moscow presses on. The collapse hands ammunition back to those who argue only a battlefield outcome - not Russian promises - can end the war.",
      },
    ],
    insight: [
      [{ id: 'eastern_eu', text: 'Eastern Europe' }, " (74%) still sits 59 points above the ", { id: 'broad_public', text: 'Broad Public' }, " (15%) - the widest divide on the war."],
      ["The ceasefire cost ", { id: 'western_eu', text: 'Western Europe' }, " 15 points as they embraced the off-ramp; ", { id: 'eastern_eu', text: 'Eastern Europe' }, " rebounded when the truce collapsed."],
    ],
  },

  // ── t8: Western support for Ukraine should continue for as long as it takes
  // Key split: Eastern delegates unwavering; public shows war fatigue.
  t8: {
    cohorts: [
      {
        id: 'eastern_eu', color: C.green,
        label:      { en: 'Eastern Europe',  de: 'Osteuropa',             fr: "Europe de l'Est"   },
        labelShort: { en: 'Eastern EU',      de: 'Osteuropa',             fr: 'Europe Est'        },
        blurb:  { en: 'Conference delegates from Poland, the Baltic states, Finland and Romania.' },
        source: { en: 'Verified on site by the MSC' },
        preV: 94, postV: 85,
        series: [
          { t: 0.00, v: 90 }, { t: 0.55, v: 89 }, { t: 0.62, v: 88 },
          { t: 1.00, v: 88 }, { t: 1.50, v: 87 }, { t: 2.00, v: 87 },
        ],
      },
      {
        id: 'western_eu', color: C.blue,
        label:      { en: 'Western Europe',  de: 'Westeuropa',            fr: "Europe de l'Ouest" },
        labelShort: { en: 'Western EU',      de: 'Westeuropa',            fr: 'Europe Ouest'      },
        blurb:  { en: 'Conference delegates from Germany, France, Italy, Spain and the Benelux states.' },
        source: { en: 'Verified on site by the MSC' },
        preV: 83, postV: 63,
        series: [
          { t: 0.00, v: 78 }, { t: 0.55, v: 76 }, { t: 0.62, v: 67 },
          { t: 1.00, v: 66 }, { t: 1.50, v: 66 }, { t: 2.00, v: 66 },
        ],
      },
      {
        id: 'young_public', color: C.amber,
        label:      { en: 'Students',    de: 'Studierende',  fr: 'Étudiants'      },
        labelShort: { en: 'Students',    de: 'Studierende',            fr: 'Étudiants'        },
        blurb:  { en: 'Students from universities across Europe.' },
        source: { en: 'Selected & verified by the MSC' },
        preV: 69, postV: 54,
        series: [
          { t: 0.00, v: 64 }, { t: 0.55, v: 63 }, { t: 0.62, v: 58 },
          { t: 1.00, v: 57 }, { t: 1.50, v: 57 }, { t: 2.00, v: 57 },
        ],
      },
      {
        id: 'broad_public', color: C.salmon,
        label:      { en: 'Broad Public',    de: 'Breite Öffentlichkeit', fr: 'Grand public'      },
        labelShort: { en: 'Broad Public',    de: 'Breite Öff.',           fr: 'Grand pub.'        },
        blurb:  { en: 'Members of the public of all ages following the conference.' },
        source: { en: 'Phone-verified representative sample' },
        preV: 47, postV: 30,
        series: [
          { t: 0.00, v: 42 }, { t: 0.55, v: 40 }, { t: 0.62, v: 34 },
          { t: 1.00, v: 33 }, { t: 1.50, v: 33 }, { t: 2.00, v: 32 },
        ],
      },
    ],
    globalEvents: [
      {
        id: 'g1', t: 0.55, tier: 1, demo: true,
        label: 'Trump: Ceasefire Push', date: 'Jan 2025',
        note: "Trump signals readiness to reduce US support and calls for an immediate ceasefire, raising doubts about Western resolve and fuelling war fatigue in domestic audiences.",
      },
    ],
    insight: [
      [{ id: 'eastern_eu', text: 'Eastern Europe' }, " holds firm at 87%; the ", { id: 'broad_public', text: 'Broad Public' }, " has fallen to 32% - a 55-point gap."],
      ["Trump's January ceasefire push cost Western European delegates 12 points in a single month."],
    ],
  },

};
